/**
 * How a page describes itself: the tab title, the description, the share card's text.
 *
 * Shared, because three places have to agree. The server writes these into the HTML for crawlers
 * and share-card scrapers, which never run JavaScript; the client rewrites them when a visitor
 * navigates inside the app; and both must produce the same string, or the title changes when you
 * click a link to the page you are already on.
 */

export const SITE_NAME = 'Midtfyns Festival';

/** The front page keeps the tagline — it is the one page whose title is about the festival itself. */
export const SITE_TAGLINE = 'Midtfyns Festival – non-profit drevet af græsrødder og ildsjæle';

/** Google cuts a title around here; past it the suffix is what gets dropped. */
const TITLE_MAX = 60;

/** And a description around here. */
const DESCRIPTION_MAX = 160;

/**
 * The tab title for a page: "Program – Midtfyns Festival", and the tagline on the front page
 * (Klaus, 20/9). A title that already names the festival is left alone rather than saying it twice.
 */
export function pageTitle(title, { isHome = false } = {}) {
  const trimmed = String(title ?? '').trim();
  if (isHome || trimmed === '') return SITE_TAGLINE;
  if (trimmed.toLowerCase().includes(SITE_NAME.toLowerCase())) return trimmed;
  return `${trimmed} – ${SITE_NAME}`;
}

/**
 * The named entities a rich-text editor actually produces. Danish text is the reason this list is
 * not just the XML five: an editor that writes `&oslash;` would otherwise put "F&oslash;rste" in a
 * search result, and there is no DOM on the server to decode it for us.
 */
const NAMED_ENTITIES = {
  nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'",
  aelig: 'æ', oslash: 'ø', aring: 'å', AElig: 'Æ', Oslash: 'Ø', Aring: 'Å',
  eacute: 'é', Eacute: 'É', hellip: '…', ndash: '–', mdash: '—',
  laquo: '«', raquo: '»', bull: '•', copy: '©', deg: '°', euro: '€', pound: '£',
};

/** Everything a tag or an entity reference was hiding, as plain text on one line. */
export function htmlToText(html) {
  return String(html ?? '')
    // A block tag is a word boundary; without this "…slut.<p>Næste" becomes one word.
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, code) => String.fromCodePoint(Number(code)))
    // Case-sensitive: &Oslash; and &oslash; are different characters.
    .replace(/&([a-z]+);/gi, (match, name) => NAMED_ENTITIES[name] ?? match)
    .replace(/\s+/g, ' ')
    .trim();
}

/** Shortens on a word boundary, with an ellipsis, or returns it unchanged when it already fits. */
export function truncate(text, max) {
  const clean = String(text ?? '').trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[.,;:–-]$/, '') + '…';
}

/** A description for the head: plain text, one line, short enough to survive being shown. */
export function metaDescription(source) {
  return truncate(htmlToText(source), DESCRIPTION_MAX);
}

/** A title short enough that the suffix is not what gets cut off. */
export function clampTitle(title) {
  return truncate(title, TITLE_MAX);
}

/**
 * The festival's name, when a heading does not already contain it — and null when it does.
 *
 * The front page's headline is a campaign line for whatever phase the festival is in, and a
 * subpage's is a single word. Neither says which festival, which is the first thing a page's one
 * heading should say to a screen reader or a search engine.
 */
export function headingPrefix(title) {
  const trimmed = String(title ?? '').trim();
  if (trimmed.toLowerCase().includes(SITE_NAME.toLowerCase())) return null;
  return SITE_NAME;
}
